/**
 * GameContext - Zentrales State Management für das Spiel
 *
 * Ersetzt die 65+ useState Hooks in App.js durch einen zentralen,
 * vorhersagbaren State-Container.
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { gameReducer, initialGameState } from './reducers/gameReducer';

// Contexts
const GameStateContext = createContext(null);
const GameDispatchContext = createContext(null);

/**
 * GameProvider - Wrapper-Komponente für Game State
 */
export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(gameReducer, initialGameState);

  return (
    <GameStateContext.Provider value={state}>
      <GameDispatchContext.Provider value={dispatch}>
        {children}
      </GameDispatchContext.Provider>
    </GameStateContext.Provider>
  );
}

/**
 * useGameState - Hook zum Lesen des Game States
 */
export function useGameState() {
  const context = useContext(GameStateContext);
  if (context === null) {
    throw new Error('useGameState must be used within a GameProvider');
  }
  return context;
}

/**
 * useGameDispatch - Hook zum Dispatchen von Actions
 */
export function useGameDispatch() {
  const context = useContext(GameDispatchContext);
  if (context === null) {
    throw new Error('useGameDispatch must be used within a GameProvider');
  }
  return context;
}

/**
 * useGame - Kombinierter Hook für State und Dispatch
 */
export function useGame() {
  const state = useGameState();
  const dispatch = useGameDispatch();
  return { state, dispatch };
}

/**
 * usePlayerState - Hook für spielerspezifischen State
 */
export function usePlayerState(playerIndex = null) {
  const state = useGameState();
  const index = playerIndex ?? state.myPlayerIndex;

  if (index === null || index === undefined) {
    return null;
  }

  return {
    hand: state.hands?.[index] || [],
    coins: state.coins?.[index] ?? 0,
    points: state.points?.[index] ?? 0,
    mult: state.mult?.[index] ?? 1,
    jokers: state.jokers?.[index] || [],
    isCurrentPlayer: state.currentPlayer === index,
    hasFinished: state.rankings?.includes(index) ?? false,
    finishPosition: state.rankings?.indexOf(index) ?? -1,
  };
}

/**
 * useGameActions - Hook mit vordefinierten Actions
 */
export function useGameActions() {
  const dispatch = useGameDispatch();

  const selectCard = useCallback((card) => {
    dispatch({ type: 'SELECT_CARD', payload: card });
  }, [dispatch]);

  const deselectCard = useCallback((card) => {
    dispatch({ type: 'DESELECT_CARD', payload: card });
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, [dispatch]);

  const updateGameState = useCallback((serverState) => {
    dispatch({ type: 'GAME_STATE_UPDATE', payload: serverState });
  }, [dispatch]);

  const setPhase = useCallback((phase) => {
    dispatch({ type: 'SET_PHASE', payload: phase });
  }, [dispatch]);

  const setConnection = useCallback((connected, playerId = null) => {
    dispatch({
      type: 'SET_CONNECTION',
      payload: { connected, playerId }
    });
  }, [dispatch]);

  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, [dispatch]);

  return {
    selectCard,
    deselectCard,
    clearSelection,
    updateGameState,
    setPhase,
    setConnection,
    resetGame,
  };
}

export default GameProvider;
